import React, {useEffect, useState} from "react";
import {Grid, Typography} from "@mui/material";
import SpaceUsageLoader from "../../components/deprecatedUikit/SpaceUsageCard/SpaceUsageLoader";
import GroupSummaries from "../../components/GroupSummaries/GroupSummaries/GroupSummaries";
import GroupSummariesLoader from "../../components/GroupSummaries/GroupSummaries/GroupSummariesLoader";
import BuyerSpaceUsageFilters from "../../components/spaceUsage/buyer/BuyerSpaceUsageFilters";
import NoMatchingInvoicesFound from '../../components/NoMatchingInvoicesFound';
import BuyerSpaceUsageWithPagination from "../../components/spaceUsage/BuyerSpaceUsageWithPagination";
import InventoryRecords from "../inventory-records/InventoryRecords";
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import BuyerSpaceUsageExportCSVs from "../../components/spaceUsage/buyer/BuyerSpaceUsageExportCSVs";
import {
  requestBuyerSpaceUsagePaginated,
  requestCalculateBuyerSpaceUsageSummary
} from "../../requests/reports/space-usage/buyer-space-usage-requests";
import moment from "moment";
import {DateFormats} from "../../components/constants/securspace-constants";
import {useSnackbar} from "../../context/snackbar-context";
import {getErrorMessage} from "../../util/NetworkErrorUtil";
import {clampPage, clampSize} from "../../util/UrlPagingParamUtils";

const defaultSummariesData = {
    "totalOverageSpaces": 0,
    "totalChargedOverages": 0,
    "totalUnchargedOverages": 0
};

const BuyerSpaceUsageReport = ({ account }) => {
    const { overageInvoiceNumber, initBookingNumber, initStartDate, initEndDate, locationId } = useUrlQueryParams();
    const { snackbarShowMessage } = useSnackbar();

    const {sessionStorage} = window;

    const sessionStart = sessionStorage?.getItem("startDate")?.toString();
    const sessionEnd = sessionStorage?.getItem("endDate")?.toString();
    const savedStartDate = initStartDate ? initStartDate : sessionStart ? moment(sessionStart).format(DateFormats.DAY).toString() : moment().add(-1, 'months').format(DateFormats.DAY).toString();
    const savedEndDate = initEndDate ? initEndDate : sessionEnd ? moment(sessionEnd).format(DateFormats.DAY).toString() : moment().format(DateFormats.DAY).toString();
    const savedBookingNumber = initBookingNumber ? initBookingNumber : sessionStorage.getItem("bookingNumber");
    const savedInvoiceNumber = overageInvoiceNumber ? overageInvoiceNumber : sessionStorage.getItem("invoiceNumber");
    const savedLocationId = locationId ? locationId : sessionStorage.getItem("locationId");
    const savedSortBy = sessionStorage.getItem("sortBy");
    const savedSortDir = sessionStorage.getItem("sortDir");
    const savedPage = sessionStorage.getItem("page");
    const savedSize = sessionStorage.getItem("size");

    // TODO: TS Migration - this is a BookedSpaceCalendarView type
    const [spaceUsageData, setSpaceUsageData] = useState([]);
    const [summaries, setSummaries] = useState(defaultSummariesData);
    // sessionStorage returns strings, and may hold a negative page written before this was fixed
    const [page, setPage] = useState(clampPage(savedPage));
    const [size, setSize] = useState(clampSize(savedSize, 50));
    const [totalNumberResults, setTotalNumberResults] = useState(0);
    const [selectedLocation, setSelectedLocation] = useState({ locationName: "", id: (savedLocationId || "" )});
    const [endDate, setEndDate] = useState(savedEndDate);
    const [startDate, setStartDate] = useState(savedStartDate);
    const [bookingNumber, setBookingNumber] = useState(savedBookingNumber || "");
    const [invoiceNumber, setInvoiceNumber] = useState(savedInvoiceNumber || "");
    const [sortBy, setSortBy] = useState(savedSortBy || "LOCATION_NAME");
    const [inventoryRecordToShow, setInventoryRecordToShow] = useState();
    const [loading, setLoading] = useState(false);
    const [sortDir, setSortDir] = useState(savedSortDir || "asc");
    const [query, setQuery] = useState();

    useEffect(() => {
        const { id } = account;
        const checkLength = (number) => number?.length >= 3 ? number : "";

        if (id) {
            setLoading(true);
            setQuery({
                buyerAccountId: id,
                page: page,
                size: size,
                startDate: startDate,
                endDate: endDate,
                sortBy: sortBy,
                locationId: selectedLocation?.id,
                bookingNumber: checkLength(bookingNumber),
                invoiceNumber: checkLength(invoiceNumber),
                sortDir
            });
        }

    }, [account, page, size, startDate, endDate, selectedLocation?.id, bookingNumber, invoiceNumber, sortBy, sortDir]);

    useEffect( () => {

        const {id} = account;
        const checkLength = (number) => number?.length >= 3 ? number : "";

        if (id) {
            getSummary({
                buyerAccountId: id,
                startDate: startDate,
                endDate: endDate,
                locationId: selectedLocation?.id,
                bookingNumber: checkLength(bookingNumber),
                invoiceNumber: checkLength(invoiceNumber)
            }).then(({body}) => {
                setSummaries(body || defaultSummariesData);
            }).catch(err => snackbarShowMessage(getErrorMessage(err), "error", 15000));
        }

    }, [account, startDate, endDate, selectedLocation?.id, bookingNumber, invoiceNumber, snackbarShowMessage]);

    useEffect(() => {
        if (query) {
            getData(query)
                .then(({body}) => {
                    const {content} = body;
                    setTotalNumberResults(body.count);
                    setSpaceUsageData(content);
                    setLoading(false);
                })
                .catch(err => snackbarShowMessage(getErrorMessage(err), "error", 15000))
                .finally(() => setLoading(false));
        }
    }, [query, snackbarShowMessage]);

    const getSummary = async (params) => {
        return requestCalculateBuyerSpaceUsageSummary(params);
    }

    const getData = async (params) => {
        return requestBuyerSpaceUsagePaginated(params);
    };

    const handlePageChange = (_, selectedPage) => {
        setPage(clampPage(selectedPage))
    }

    const handleSizeChange = (event) => {
        setSize(clampSize(event.target.value, 50))
    }

    const closeInventoryRecords = () => {
        setInventoryRecordToShow(null)
    }

    const handleShowInventory = (inventory) => {
        sessionStorage.setItem("locationId", selectedLocation.id || "")
        sessionStorage.setItem("page", query.page)
        sessionStorage.setItem("size", query.size)
        sessionStorage.setItem("startDate", query.startDate)
        sessionStorage.setItem("endDate", query.endDate)
        sessionStorage.setItem("sortBy", query.sortBy)
        sessionStorage.setItem("bookingNumber", query.bookingNumber)
        sessionStorage.setItem("invoiceNumber", query.invoiceNumber)
        sessionStorage.setItem("sortDir", query.sortDir)

        setInventoryRecordToShow(inventory)
    }

    const filterState = {filter: "inventory"}

    return (
        <Grid item container flexDirection={'column'}>
            <Grid item marginBottom={"2em"}>
                <Typography variant={'h5'} component={'h1'}>Space Usage</Typography>
            </Grid>
            {
                inventoryRecordToShow ?
                    <InventoryRecords
                        inventoryDate={inventoryRecordToShow.calendarDate}
                        spaceUtilizationRecord={inventoryRecordToShow}
                        closeInventoryRecords={closeInventoryRecords}
                        filterState={filterState}
                    />
                    :
                    <Grid item container xs>
                        <Grid item container p={2} mb={4}>
                            <BuyerSpaceUsageFilters
                                setSelectedLocation={setSelectedLocation}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                setBookingNumber={setBookingNumber}
                                setInvoiceNumber={setInvoiceNumber}
                                setSortBy={setSortBy}
                                setSortDir={setSortDir}
                                savedStartDate={savedStartDate}
                                savedEndDate={savedEndDate}
                                savedLocationId={savedLocationId}
                                savedBookingNumber={bookingNumber}
                                savedSortBy={sortBy}
                                savedInvoiceNumber={invoiceNumber}
                            />
                        </Grid>
                        {
                            !loading && spaceUsageData?.length > 0 && <Grid item container justifyContent={"space-between"} mb={6} gap={2}>
                                <Grid item lg={6}>
                                    <Typography
                                        variant='body2'
                                        sx={{ textTransform: 'uppercase', color: '#000'}}
                                        component='p'
                                    >
                                        {selectedLocation?.locationName || 'All'} (SHOWING {spaceUsageData.length} RECORDS IN GROUP)
                                    </Typography>
                                </Grid>
                                <BuyerSpaceUsageExportCSVs
                                    item
                                    query={query}
                                />
                            </Grid>
                        }
                        {
                            (loading || spaceUsageData?.length > 0) && <Grid item mb={4} xs={12}>
                                {
                                    loading ? <GroupSummariesLoader /> : <GroupSummaries
                                        groupSummaryFields={[
                                            {
                                                title: 'Total Overage Spaces',
                                                value: summaries.totalOverageSpaces,
                                            },
                                            {
                                                title: 'Total Charged Overages',
                                                value: summaries.totalChargedOverages,
                                            },
                                            {
                                                title: 'Total Un-Charged Overages',
                                                value: summaries.totalUnchargedOverages,
                                            },
                                        ]}
                                    />
                                }
                            </Grid>
                        }
                        {
                            loading ? Array.from({ length: 12 }).map((_, index) => (
                                <SpaceUsageLoader key={index} />
                            )) : spaceUsageData?.length > 0 ?
                                <BuyerSpaceUsageWithPagination
                                    spaceUsageData={spaceUsageData}
                                    account={account}
                                    size={size}
                                    page={page}
                                    totalNumberResults={totalNumberResults}
                                    handlePageChange={handlePageChange}
                                    handleSizeChange={handleSizeChange}
                                    setInventoryRecordToShow={handleShowInventory}
                                />
                                : <NoMatchingInvoicesFound item message="No matching records found" />
                        }
                    </Grid>
            }
        </Grid>
    )
}

export default BuyerSpaceUsageReport;
